import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, BarChart3, Bug, CheckCircle, CircleDot, Copy, Eye, EyeOff, Flame, MessageSquare, Shield, ShieldAlert, ShieldCheck, UserCheck } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';

const SonarCloudIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [exampleProject, setExampleProject] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [organization, setOrganization] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [connectOrganization, setConnectOrganization] = useState('');
  const [connectApiToken, setConnectApiToken] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/sonarcloud`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getSonarCloudStatus();
      if (data?.connected) {
        setConnected(true);
        setOrganization(data.organization || '');
        setWebhookSecret(data.webhook_secret || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectSonarCloud({
        organization: connectOrganization.trim(),
        api_token: connectApiToken,
      });
      setSuccessMsg('SonarCloud connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectSonarCloud();
      setConnected(false);
      setSuccessMsg('SonarCloud disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testSonarCloud();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to SonarCloud (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <SonarCloudIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  SonarCloud
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>React to quality gate results, list open vulnerabilities and security hotspots, and fetch code quality metrics from SonarCloud</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium truncate">{organization}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Webhook Events Received</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Webhook</CardTitle>
            <CardDescription>Register a SonarCloud webhook (organization- or project-level) to trigger workflows after every analysis. Actions work without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyValue('url', webhookUrl)} className="gap-1 shrink-0">
                  {copied === 'url' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {webhookSecret && (
              <div>
                <Label className="text-sm">Webhook Secret</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{showSecret ? webhookSecret : '••••••••••••••••••••••••'}</code>
                  <Button variant="outline" size="sm" onClick={() => setShowSecret(s => !s)} className="gap-1 shrink-0" aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyValue('secret', webhookSecret)} className="gap-1 shrink-0">
                    {copied === 'secret' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'secret' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              In SonarCloud, open your organization and select <strong className="text-foreground">Webhooks</strong> in the left sidebar (for a single project: <strong className="text-foreground">Administration → Webhooks</strong>), then click <strong className="text-foreground">Create</strong>. Set the URL above and paste the secret. SonarCloud signs each delivery with HMAC-SHA256 (<code className="bg-muted px-1 rounded text-[11px] font-mono">X-Sonar-Webhook-HMAC-SHA256</code> header) and Kestrel verifies it.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these SonarCloud triggers and actions in your workflows to automate quality-gate response and vulnerability triage.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (webhook)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Quality Gate Failed', desc: 'Triggers when an analysis completes with the quality gate in ERROR.', Icon: ShieldAlert },
                  { name: 'Quality Gate Passed', desc: 'Triggers when an analysis completes with the quality gate OK.', Icon: ShieldCheck },
                  { name: 'Analysis Completed (any result)', desc: 'Triggers when an analysis finishes with any quality gate result.', Icon: CircleDot },
                  { name: 'Analysis Failed', desc: 'Triggers when the analysis task itself fails (broken scanner run).', Icon: AlertTriangle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get Quality Gate Status', desc: 'Fetch a project\u2019s quality gate status with the failing conditions.', Icon: ShieldCheck },
                  { name: 'List Issues', desc: 'List open bugs, vulnerabilities, and code smells by severity and type — optionally only new code.', Icon: Bug },
                  { name: 'List Security Hotspots', desc: 'List security-sensitive code awaiting review, by review status.', Icon: Flame },
                  { name: 'Get Project Metrics', desc: 'Fetch metric values like bugs, coverage, and duplication.', Icon: BarChart3 },
                  { name: 'Transition Issue', desc: 'Confirm, resolve, reopen, or mark an issue false positive / won\u2019t fix.', Icon: CheckCircle },
                  { name: 'Assign Issue', desc: 'Assign an issue to a user so new findings get an owner automatically.', Icon: UserCheck },
                  { name: 'Comment on Issue', desc: 'Record triage decisions or incident context on an issue.', Icon: MessageSquare },
                  { name: 'Review Security Hotspot', desc: 'Mark a hotspot Safe, Fixed, or Acknowledged — or send it back to review.', Icon: Shield },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When the quality gate fails on the{' '}
              <input
                value={exampleProject}
                onChange={e => setExampleProject(e.target.value)}
                placeholder="project key"
                style={{ width: `${Math.max(exampleProject.length || 11, 11)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}SonarCloud project, list the new blocker and critical vulnerabilities and post them to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleProject || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When the quality gate fails on the ${exampleProject} SonarCloud project, list the new blocker and critical vulnerabilities and post them to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <SonarCloudIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle>SonarCloud</CardTitle>
                <CardDescription>Connect SonarCloud to react to quality gate results, list open vulnerabilities and security hotspots, and fetch code quality metrics in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="sonarcloud-org" className="text-sm">Organization Key</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                The key in your SonarCloud URL — <code className="bg-muted px-1 rounded text-[11px] font-mono">sonarcloud.io/organizations/&lt;key&gt;</code> — or under <strong className="text-foreground">Administration → Organization settings</strong>.
              </p>
              <Input
                id="sonarcloud-org"
                value={connectOrganization}
                onChange={e => setConnectOrganization(e.target.value)}
                placeholder="my-org"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="sonarcloud-token" className="text-sm">API Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the SonarCloud Web API with a <strong className="text-foreground">user token</strong>. Create one in SonarCloud under <strong className="text-foreground">My Account → Access Tokens → Generate Token</strong>.
              </p>
              <Input
                id="sonarcloud-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="SonarCloud API token"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the SonarCloud API.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken || !connectOrganization.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect SonarCloud
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default SonarCloudIntegration;
